import React, { useCallback, useState } from 'react';
import { Box, Button, Typography, LinearProgress, Paper } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

interface ImageUploaderProps {
  onUploadComplete?: () => void;
  onUploadError?: (error: string) => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUploadComplete,
  onUploadError,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((file) =>
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      handleFileUpload(files);
    }

    e.target.value = '';
  }, []);

  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('image', file);
      });

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
          setUploadStatus(`Uploading... ${Math.round(percentComplete)}%`);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          setUploadStatus('✅ Upload successful!');
          setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
            if (onUploadComplete) {
              onUploadComplete();
            }
          }, 1500);
        } else {
          throw new Error('Upload failed');
        }
      });

      xhr.addEventListener('error', () => {
        const errorMsg = 'Upload failed. Please check your connection.';
        setUploadStatus(errorMsg);
        if (onUploadError) {
          onUploadError(errorMsg);
        }
        setTimeout(() => {
          setUploading(false);
        }, 2000);
      });

      const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('auth_token');

      xhr.open('POST', `${baseURL}/v1/media/upload`);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Upload failed';
      setUploadStatus(errorMsg);
      if (onUploadError) {
        onUploadError(errorMsg);
      }
      setTimeout(() => {
        setUploading(false);
      }, 2000);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Upload Images
      </Typography>

      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragging ? 'action.hover' : 'transparent',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'text.secondary',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            color: 'text.secondary',
            mb: 2,
          }}
        />
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
          Drag and drop images here
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          or click to browse files
        </Typography>
        <Button
          variant="contained"
          component="label"
          disabled={uploading}
          sx={{ textTransform: 'none' }}
        >
          Browse Files
          <input
            type="file"
            multiple
            accept="image/*"
            hidden
            onChange={handleFileSelect}
          />
        </Button>
      </Box>

      {uploading && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: 8, borderRadius: 1, mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {uploadStatus}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
